import { useEffect, useRef, useState } from "react";
import styles from "./Preview.module.css";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useTranslation } from "react-i18next";

interface IPreview {
    name: string;
    image: string;
    isSplitViewDisabled: boolean;
    isPublic: boolean;
    portionCount: number;
    preparationTime: number;
    ingredientsPosition(height: number): void;
}

const Preview: React.FC<IPreview> = (props) => {
    const [name, setName] = useState(props.name);
    const isMobile = useSelector((state: RootState) => state.client.isMobile);
    const imageRef = useRef<HTMLImageElement>(null);
    const { t } = useTranslation();

    useEffect(() => {
        if (imageRef.current) {
            if (!isMobile) {
                // desktop
                props.ingredientsPosition(imageRef.current.getBoundingClientRect().top);
            } else if (!props.isSplitViewDisabled) {
                // mobile - split view
                props.ingredientsPosition(imageRef.current.getBoundingClientRect().bottom);
            }
        }
    }, [props.image, props.isSplitViewDisabled]);

    useEffect(() => {
        if (name.length > 90) {
            setName(name.substring(0, 90) + "...");
        }
    }, [name]);

    return (
        <div className={styles.container}>
            <h1 className={styles.name}>{name}</h1>
            <div className={props.isSplitViewDisabled ? styles.referenceMobile : styles.referenceDesktop}>
                {!props.isPublic && (
                    <div className={styles.circle}>
                        <div className={styles.icon}>
                            <i className="bi bi-lock-fill"></i>
                        </div>
                    </div>
                )}
            </div>
            <div className={isMobile ? styles.imageMobile : styles.imageDesktop}>
                <img className={styles.borderRadius} src={props.image} ref={imageRef} alt={name} />
                <div className={styles.detailsContainer}>
                    <i className="bi bi-stopwatch-fill">
                        {" "}
                        {props.preparationTime} {t("common.min")}
                    </i>
                    {props.portionCount && props.portionCount != 0 && (
                        <span>
                            <span> | </span>
                            <svg
                                fill="#ffffff"
                                height="18px"
                                width="18px"
                                version="1.2"
                                baseProfile="tiny"
                                id="Layer_1"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 256.00 325.00"
                                stroke="#ffffff"
                                stroke-width="0.00256"
                                transform="matrix(1, 0, 0, 1, 0, 0)rotate(0)"
                            >
                                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                                <g
                                    id="SVGRepo_tracerCarrier"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke="#CCCCCC"
                                    stroke-width="1.536"
                                ></g>
                                <g id="SVGRepo_iconCarrier">
                                    {" "}
                                    <path
                                        id="XMLID_3_"
                                        d="M255.3,127c-3.1,51.4-36.3,94.5-82.3,111.7V256H83v-17.3C37,221.5,3.8,178.4,0.7,127l171.1-0.1 M163.6,79.4 c0,7.8,3.2,12,5.7,15.3c0.7,1,1.4,1.9,2,2.8c0.9,1.4,2.3,2.1,3.8,2.1c0.8,0,1.6-0.2,2.4-0.7c2.1-1.3,2.8-4.1,1.5-6.2 c-0.8-1.3-1.7-2.4-2.5-3.5c-2.2-3-3.9-5.1-3.9-9.9c0-4.8,1.6-6.9,3.9-9.9c2.5-3.3,5.7-7.5,5.7-15.3s-3.2-12-5.7-15.3l-0.5-0.6 c-1.5-2-4.3-2.4-6.3-0.9c-2,1.5-2.4,4.3-0.9,6.3l0.5,0.6c2.2,3,3.9,5.1,3.9,9.9c0,4.8-1.6,6.9-3.9,9.9 C166.7,67.4,163.6,71.6,163.6,79.4z M115.7,71c0,11.2,4.4,16.9,8.3,22c1.2,1.5,2.2,2.9,3.2,4.5c0.9,1.4,2.3,2.1,3.8,2.1 c0.8,0,1.6-0.2,2.4-0.7c2.1-1.3,2.8-4.1,1.5-6.2c-1.2-1.9-2.5-3.6-3.7-5.2c-3.4-4.5-6.4-8.4-6.4-16.5s3-12,6.4-16.5 c3.9-5.1,8.3-10.8,8.3-22c0-11.1-4.4-16.9-8.3-22l-0.7-0.9c-1.5-2-4.3-2.4-6.3-0.9c-2,1.5-2.4,4.3-0.9,6.3l0.7,1 c3.4,4.5,6.4,8.4,6.4,16.5c0,8.1-3,12-6.4,16.5C120.1,54.1,115.7,59.9,115.7,71z M72.9,79.4c0,7.8,3.2,12,5.7,15.3 c0.7,1,1.4,1.9,2,2.8c0.9,1.4,2.3,2.1,3.8,2.1c0.8,0,1.6-0.2,2.4-0.7c2.1-1.3,2.8-4.1,1.5-6.2c-0.8-1.3-1.7-2.4-2.5-3.5 c-2.2-3-3.9-5.1-3.9-9.9c0-4.8,1.6-6.9,3.9-9.9c2.5-3.3,5.7-7.5,5.7-15.3c0-7.8-3.2-12-5.7-15.3l-0.5-0.6c-1.5-2-4.3-2.4-6.3-0.9 c-2,1.5-2.4,4.3-0.9,6.3l0.5,0.6c2.2,3,3.9,5.1,3.9,9.9c0,4.8-1.6,6.9-3.9,9.9C76.1,67.4,72.9,71.6,72.9,79.4z"
                                    ></path>{" "}
                                </g>
                            </svg>
                            <i> {props.portionCount}</i>
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Preview;
